import { logSignupFormAnalyticsSchema as logSignupFormAnalytics } from "@redotech/redo-marketing-common/schemas/view-signup-form-schema";
import { BrandKit } from "@redotech/redo-model/brand-kit";
import { SignupForm as ISignupForm } from "@redotech/redo-model/signup-form-schema";
import { BrandKitButton } from "@redotech/redo-web/brand-kit-button";
import { Flex } from "@redotech/redo-web/flex";
import { SignupForm } from "@redotech/redo-web/marketing/signup-form";
import { getNextStepIndex } from "@redotech/redo-web/marketing/signup-form/utils";
import { MerchantCustomizableModal } from "@redotech/redo-web/storefront/merchant-customizable-modal";
import { ANONYMOUS_SHOPPER_ID_KEY } from "@redotech/redo-web/utils/shared-conf";
import { RedoShopifyRpcClient } from "@redotech/shopify-sdk/rpc/client";
import { submitBackInStockForm } from "@redotech/shopify-sdk/rpc/schema/forms/submit-back-in-stock-form";
import { memo, useCallback, useState } from "react";

/**
 * Modal component for the Back in Stock form
 */
export const BackInStockFormModal = memo(function BackInStockFormModal({
  brandKit,
  client,
  form,
  formId,
  shopifyCustomerId,
  productId,
  variantId,
}: {
  brandKit: BrandKit;
  client?: RedoShopifyRpcClient;
  form: ISignupForm;
  formId: string;
  shopifyCustomerId: string | null;
  productId: string | null;
  variantId: string | null;
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [open, setOpen] = useState(false);

  const submitStep = useCallback(
    async ({
      stepIndex,
      formData,
    }: {
      stepIndex: number;
      formData: Record<string, string>;
    }) => {
      if (
        client &&
        productId &&
        variantId &&
        (formData.email || formData.phone)
      ) {
        const result = await client(submitBackInStockForm, {
          email: formData.email || undefined, // Don't pass empty string
          phone: formData.phone,
          productId,
          variantId,
        });

        if (!result.success) {
          console.error("Failed to submit form step", result);
        } else {
          const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);

          if (anonymousId) {
            await client(logSignupFormAnalytics, {
              anonymousId,
              shopifyCustomerId,
              formId,
              type: "submit",
            });
          }
        }
      }

      const nextStepIndex = getNextStepIndex(form, false, stepIndex + 1);
      const hasNextStep = nextStepIndex < form.steps.length;

      if (hasNextStep) {
        setCurrentStep(nextStepIndex);
      } else {
        setCurrentStep(0);
        setOpen(false);
        setCompleted(true);
      }
    },
    [form, formId, client, shopifyCustomerId, productId, variantId],
  );

  const openModal = useCallback(async () => {
    if (open) return;
    setOpen(true);

    const initialStep = getNextStepIndex(form, false, 0);
    setCurrentStep(initialStep);

    const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);

    if (client && formId && anonymousId) {
      await client(logSignupFormAnalytics, {
        shopifyCustomerId,
        formId,
        type: "view",
        anonymousId,
      });
    }
  }, [form, open, client, formId, shopifyCustomerId]);

  const opacityHex = form
    ? Math.floor((form.theme.overlay.opacityPercent / 100) * 255)
        .toString(16)
        .padStart(2, "0")
    : 0;

  return (
    <Flex justify="center" py="lg" w="full">
      <div style={{ width: "100%" }}>
        {form.pdpButton && (
          <BrandKitButton
            brandKit={brandKit}
            elementOverride={form.pdpButton.styleOverride}
            globalOverride={
              form.theme.overrides.buttons.enabled
                ? form.theme.overrides.buttons
                : undefined
            }
            hierarchy={form.pdpButton.hierarchy}
            onClick={openModal}
            pending={false}
            text={form.pdpButton.text}
            type="submit"
          />
        )}
      </div>
      {form && (
        <MerchantCustomizableModal
          backdropColor={form.theme.overlay.color + opacityHex}
          fullscreen={form.layout.type === "fullscreen"}
          onClose={() => setOpen(false)}
          open={open && !completed}
        >
          <SignupForm
            brandKit={brandKit}
            currentStep={currentStep}
            form={form}
            onClose={() => setOpen(false)}
            onSubmitStep={submitStep}
          />
        </MerchantCustomizableModal>
      )}
    </Flex>
  );
});
